"use client";

import {
  getCribnoshAvatarUrl,
  getCribnoshDefaultAvatarUrl,
  normalizeCribnoshAvatarId,
  resolveCribnoshAvatarBaseUrl,
} from "@shared/avatars/defaultAvatars";
import Image, { type ImageProps } from "next/image";
import { useEffect, useRef, useState, type CSSProperties, type ImgHTMLAttributes } from "react";

export const TRY_IT_FALLBACK_IMAGE = "/images/fallbacks/fallback-grey-meal.png";
export const TRY_IT_FALLBACK_WIDE_IMAGE = "/images/fallbacks/fallback-grey-wide.png";
export const TRY_IT_FALLBACK_AVATAR_IMAGE = getCribnoshDefaultAvatarUrl(
  resolveCribnoshAvatarBaseUrl(process.env),
);
const FALLBACK_RETRY_DELAY_MS = 5000;
const IMAGE_SKELETON_BLUR_DATA_URL =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='0%25'%3E%3Cstop offset='0%25' stop-color='%23f4f4f5'/%3E%3Cstop offset='50%25' stop-color='%23e4e4e7'/%3E%3Cstop offset='100%25' stop-color='%23f4f4f5'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='64' height='64' fill='url(%23g)'/%3E%3C/svg%3E";

type TryItImageSource =
  | string
  | TryItImageSource[]
  | {
      url?: unknown;
      src?: unknown;
      uri?: unknown;
      href?: unknown;
      image?: unknown;
      imageUrl?: unknown;
      image_url?: unknown;
      publicUrl?: unknown;
      public_url?: unknown;
      fileUrl?: unknown;
      file_url?: unknown;
      storageUrl?: unknown;
      storage_url?: unknown;
      storageId?: unknown;
      storage_id?: unknown;
      fileId?: unknown;
      file_id?: unknown;
      id?: unknown;
    }
  | null
  | undefined;

const getSourceStrings = (value: TryItImageSource): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) {
    return value.flatMap((entry) => getSourceStrings(entry));
  }
  if (!value || typeof value !== "object") return [];

  const candidates = [
    value.url,
    value.src,
    value.uri,
    value.href,
    value.image_url,
    value.imageUrl,
    value.publicUrl,
    value.public_url,
    value.fileUrl,
    value.file_url,
    value.storageUrl,
    value.storage_url,
    value.image,
    value.storageId,
    value.storage_id,
    value.fileId,
    value.file_id,
    value.id,
  ];
  return candidates.filter((entry): entry is string => typeof entry === "string");
};

const isStorageLikeImageRef = (value: string): boolean => {
  if (/^k[a-zA-Z0-9]+$/.test(value)) return true;
  if (/^storage:/i.test(value)) return true;
  if (/^\/?api\/(?:files|storage)\//i.test(value)) return true;
  return false;
};

const ABSOLUTE_URL_SCHEME_REGEX = /https?:\/\//gi;

const dedupeRepeatedAbsoluteUrl = (input: string): string => {
  const value = input.trim();
  const schemeMatches = Array.from(value.matchAll(ABSOLUTE_URL_SCHEME_REGEX));

  for (let index = 1; index < schemeMatches.length; index += 1) {
    const duplicateStart = schemeMatches[index].index;
    if (typeof duplicateStart !== "number") continue;

    const firstUrl = value.slice(0, duplicateStart);
    const secondUrl = value.slice(duplicateStart);

    if (firstUrl === secondUrl) return firstUrl;
  }

  return value;
};

const isBackendResolvedFileUrl = (value: string): boolean => {
  if (/^\/api\/files\//i.test(value)) return true;

  try {
    const parsed = new URL(value);
    return /^\/api\/files\//i.test(parsed.pathname);
  } catch {
    return false;
  }
};

const OPTIMIZED_IMAGE_HOSTS = new Set([
  "cdn.convex.cloud",
  "processor-site.cribnosh.co.uk",
  "cribnosh-media-cdn.cybermonkj.workers.dev",
  "cribnoshprodmedia.blob.core.windows.net",
  "images.unsplash.com",
  "images.pexels.com",
]);
const FILE_PATH_REGEX = /^\/api\/files\/([0-9a-z-]+)$/i;
const LEGACY_STORAGE_PATH_REGEX = /^\/api\/storage\/([0-9a-z-]+)$/i;
const LOCALHOST_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1"]);

const toCanonicalConvexFilePath = (value: string): string | null => {
  const trimmed = value.trim();
  const fileMatch = trimmed.match(FILE_PATH_REGEX);
  if (fileMatch?.[1]) return `/api/files/${fileMatch[1]}`;

  const storageMatch = trimmed.match(LEGACY_STORAGE_PATH_REGEX);
  if (storageMatch?.[1]) return `/api/files/${storageMatch[1]}`;

  return null;
};

const normalizeResolvedBackendImageUrl = (value: string): string => {
  const trimmed = value.trim();

  if (trimmed.startsWith("/")) {
    try {
      const parsed = new URL(trimmed, "http://cribnosh.local");
      const canonicalPath = toCanonicalConvexFilePath(parsed.pathname);
      if (canonicalPath) {
        return `${canonicalPath}${parsed.search}${parsed.hash}`;
      }
      return value;
    } catch {
      return value;
    }
  }

  try {
    const parsed = new URL(trimmed);
    const canonicalPath = toCanonicalConvexFilePath(parsed.pathname);
    if (!canonicalPath) return value;

    const hostname = parsed.hostname.toLowerCase();
    const isLocalHost = LOCALHOST_HOSTNAMES.has(hostname);
    const isConvexHost =
      hostname === "processor-site.cribnosh.co.uk" ||
      hostname.endsWith(".convex.cloud") ||
      hostname.endsWith(".convex.site");
    if (!isLocalHost && !isConvexHost && !hostname.endsWith(".cribnosh.co.uk")) {
      return value;
    }

    return `${canonicalPath}${parsed.search}${parsed.hash}`;
  } catch {
    return value;
  }
};

export const shouldUseNextImageOptimization = (src: string): boolean => {
  if (
    src.startsWith("/api/files/") ||
    src.startsWith("/api/storage/") ||
    src.startsWith("/api/files?") ||
    src.startsWith("/api/storage?")
  ) {
    return false;
  }

  if (src.startsWith("/") && !src.startsWith("//")) return true;
  if (src.startsWith("data:image/")) return false;

  try {
    const { hostname, pathname } = new URL(src);
    if (pathname.startsWith("/api/files/") || pathname.startsWith("/api/storage/")) return false;
    if (OPTIMIZED_IMAGE_HOSTS.has(hostname)) {
      return hostname !== "processor-site.cribnosh.co.uk" && hostname !== "cdn.convex.cloud";
    }
    if (hostname.endsWith(".convex.cloud")) {
      return false;
    }
    if (hostname.endsWith(".convex.site")) {
      return false;
    }
  } catch {
    return false;
  }

  return false;
};

export function normalizeTryItImageSrc(
  value: TryItImageSource,
  fallback = TRY_IT_FALLBACK_IMAGE,
): string {
  const candidates = getSourceStrings(value);

  for (const candidate of candidates) {
    const raw = dedupeRepeatedAbsoluteUrl(candidate);
    if (!raw) continue;

    const avatarId = normalizeCribnoshAvatarId(raw);
    if (avatarId) {
      return getCribnoshAvatarUrl(avatarId, resolveCribnoshAvatarBaseUrl(process.env));
    }

    if (
      raw.startsWith("http://") ||
      raw.startsWith("https://") ||
      raw.startsWith("data:image/")
    ) {
      return raw.startsWith("data:image/") ? raw : normalizeResolvedBackendImageUrl(raw);
    }

    if (isStorageLikeImageRef(raw)) {
      const resolvedBackendUrl = normalizeResolvedBackendImageUrl(raw);
      if (resolvedBackendUrl !== raw || isBackendResolvedFileUrl(raw)) return resolvedBackendUrl;
      continue;
    }

    if (raw.startsWith("/")) {
      return raw;
    }
  }

  return fallback;
}

type SafeTryItImageProps = Omit<ImageProps, "src" | "onError"> & {
  src: TryItImageSource;
  fallbackSrc?: string;
  showLoadingIndicator?: boolean;
  onError?: ImageProps["onError"];
};

type SafeTryItImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "onError"> & {
  src: TryItImageSource;
  fallbackSrc?: string;
  showLoadingIndicator?: boolean;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  onError?: ImgHTMLAttributes<HTMLImageElement>["onError"];
};

function useSafeTryItSource(src: TryItImageSource, fallbackSrc: string) {
  const normalizedSrc = normalizeTryItImageSrc(src, fallbackSrc);
  const normalizedFallback = normalizeTryItImageSrc(fallbackSrc, TRY_IT_FALLBACK_IMAGE);
  const [currentSrc, setCurrentSrc] = useState(normalizedSrc);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCurrentSrc(normalizedSrc);
    setRetryAttempt(0);
    setIsLoading(true);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, [normalizedSrc]);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentSrc !== normalizedFallback || normalizedSrc === normalizedFallback) {
      return;
    }

    const probe = new window.Image();
    probe.onload = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      setCurrentSrc(normalizedSrc);
    };
    probe.onerror = () => {
      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        setRetryAttempt((attempt) => attempt + 1);
      }, FALLBACK_RETRY_DELAY_MS);
    };
    probe.src = normalizedSrc;

    return () => {
      probe.onload = null;
      probe.onerror = null;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [currentSrc, normalizedFallback, normalizedSrc, retryAttempt]);

  return {
    currentSrc,
    isLoading,
    normalizedFallback,
    normalizedSrc,
    setCurrentSrc,
    setIsLoading,
  };
}

export function SafeTryItImage({
  src,
  fallbackSrc = TRY_IT_FALLBACK_IMAGE,
  showLoadingIndicator = true,
  onError,
  unoptimized,
  className,
  fill,
  loading,
  decoding,
  priority,
  placeholder,
  blurDataURL,
  onLoad,
  ...props
}: SafeTryItImageProps) {
  const { currentSrc, isLoading, normalizedFallback, setCurrentSrc, setIsLoading } =
    useSafeTryItSource(src, fallbackSrc);

  return (
    <>
      <Image
        {...props}
        className={className}
        fill={fill}
        src={currentSrc}
        decoding={decoding ?? "async"}
        loading={priority ? undefined : (loading ?? "lazy")}
        placeholder={placeholder ?? "blur"}
        blurDataURL={blurDataURL ?? IMAGE_SKELETON_BLUR_DATA_URL}
        priority={priority}
        unoptimized={shouldUseNextImageOptimization(currentSrc) ? false : unoptimized}
        onLoad={(event) => {
          setIsLoading(false);
          onLoad?.(event);
        }}
        onError={(event) => {
          if (currentSrc !== normalizedFallback) {
            setCurrentSrc(normalizedFallback);
            setIsLoading(true);
          } else {
            setIsLoading(false);
          }
          onError?.(event);
        }}
      />
      {fill && showLoadingIndicator && isLoading ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 animate-pulse bg-[linear-gradient(135deg,rgba(244,244,245,0.92),rgba(228,228,231,0.75),rgba(244,244,245,0.92))]"
        />
      ) : null}
    </>
  );
}

export function SafeTryItImg({
  src,
  fallbackSrc = TRY_IT_FALLBACK_IMAGE,
  showLoadingIndicator = true,
  onError,
  onLoad,
  className,
  containerClassName = "relative block overflow-hidden",
  containerStyle,
  loading,
  decoding,
  alt,
  ...props
}: SafeTryItImgProps) {
  const { currentSrc, isLoading, normalizedFallback, setCurrentSrc, setIsLoading } =
    useSafeTryItSource(src, fallbackSrc);

  return (
    <span className={containerClassName} style={containerStyle}>
      <img
        {...props}
        alt={alt}
        src={currentSrc}
        className={className}
        loading={loading ?? "lazy"}
        decoding={decoding ?? "async"}
        onLoad={(event) => {
          setIsLoading(false);
          onLoad?.(event);
        }}
        onError={(event) => {
          if (currentSrc !== normalizedFallback) {
            setCurrentSrc(normalizedFallback);
            setIsLoading(true);
          } else {
            setIsLoading(false);
          }
          onError?.(event);
        }}
      />
      {showLoadingIndicator && isLoading ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 animate-pulse bg-[linear-gradient(135deg,rgba(244,244,245,0.92),rgba(228,228,231,0.75),rgba(244,244,245,0.92))]"
        />
      ) : null}
    </span>
  );
}
